import axios from "axios";
import { AxiosRequest } from "../ApiCall";
import { configEnv } from "../constant";

export type TemplateScope = "global" | "org";
export type TemplateTag =
    | "Observation"
    | "Compliance"
    | "Assessment"
    | "Identity";

export interface TemplateStats {
    parameterCount: number;
    criteriaCount: number;
    observerQuestionBankItems?: number;
    presetQuestionPapers?: number;
}

export interface TemplateSummary {
    _id: string;
    templateId: string;
    scope: TemplateScope;
    organizationId?: string;
    parentTemplateId?: string;
    sector: string;
    tag: TemplateTag;
    displayName: string;
    summary?: string;
    method?: string;
    framework?: string;
    audience?: string;
    expectedOutput?: string;
    turnaround?: string;
    stats?: TemplateStats;
    presetQuestionPapers?: number;
    isStarred: boolean;
    isInstalled: boolean;
    installedAuditCategoryId: string | null;
    lastViewedAt: string | null;
    // Tier 1 trust/liquidity signals
    installCount: number;
    isVerified: boolean;
    featuredOrder?: number;
    avgRating?: number;
    ratingCount: number;
    lastReinstalledAt?: string | null;
    updatedAt?: string;
}

export interface TemplateCriterionDetail {
    slug: string;
    name: string;
    description?: string;
    expectations: Partial<{
        rating1: string;
        rating2: string;
        rating3: string;
        rating4: string;
        rating5: string;
    }>;
}

export interface TemplateParameterDetail {
    slug: string;
    order: number;
    name: string;
    description?: string;
    suggestedWeightPercent?: number;
    triggerScore: any[];
    criteria: TemplateCriterionDetail[];
}

export interface TemplatePoolCriterionDetail {
    criterionSlug: string;
    criterionName: string;
    parameterName: string;
    questionCount: number;
}

export interface TemplateDetail extends TemplateSummary {
    auditCategoryMeta: { name: string; notes?: string };
    createdByName?: string;
    parameters: TemplateParameterDetail[];
    questionPool?: {
        name: string;
        description?: string;
        totalQuestionsToSelect: number;
        criteria: TemplatePoolCriterionDetail[];
    };
    presetQuestionPapersList?: Array<{
        name: string;
        description?: string;
        parameterSlugs?: string[];
        questionCount?: number;
        estimatedMinutes?: number;
    }>;
    identityDefaults?: { enabled: boolean };
    schemaVersion?: string;
    version?: number;
    scoring?: {
        criterionScale?: {
            min: number;
            max: number;
            labels?: Record<string, string>;
        };
    };
}

export interface InstallResult {
    auditCategoryId: string;
    auditFormId: string;
    questionPoolId?: string;
    resourceIds?: string[];
}

export interface AlreadyInstalled {
    code: "ALREADY_INSTALLED";
    auditCategoryId: string;
    auditFormId?: string;
}

export type InstallResponse =
    | { kind: "installed"; data: InstallResult }
    | { kind: "alreadyInstalled"; data: AlreadyInstalled };

function authHeader() {
    const raw = localStorage.getItem("access_token");
    const token = raw ? JSON.parse(raw) : "";
    return { Authorization: `Bearer ${token}` };
}

function base() {
    return `${configEnv.baseUrl}/template-marketplace`;
}

export const templateMarketplaceApi = {
    async list(params: {
        sector?: string;
        tag?: string;
        scope?: "global" | "org" | "all";
        search?: string;
        quickAccess?: "all" | "starred" | "recent" | "mine";
        organizationId?: string;
        limit?: number;
    } = {}): Promise<TemplateSummary[]> {
        const query = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== "") query.set(k, String(v));
        });
        const qs = query.toString();
        const res = await AxiosRequest(
            "GET",
            `${base()}${qs ? `?${qs}` : ""}`,
            undefined,
            authHeader()
        );
        return res?.data?.data ?? [];
    },

    async getById(id: string, organizationId?: string): Promise<TemplateDetail> {
        const qs = organizationId
            ? `?organizationId=${encodeURIComponent(organizationId)}`
            : "";
        const res = await AxiosRequest(
            "GET",
            `${base()}/${id}${qs}`,
            undefined,
            authHeader()
        );
        return res?.data?.data;
    },

    async toggleStar(id: string, organizationId?: string): Promise<{ starred: boolean }> {
        const res = await AxiosRequest(
            "POST",
            `${base()}/${id}/star`,
            { organizationId },
            authHeader()
        );
        return res?.data?.data;
    },

    async duplicate(
        id: string,
        displayName: string,
        organizationId?: string
    ): Promise<TemplateDetail> {
        const res = await AxiosRequest(
            "POST",
            `${base()}/${id}/duplicate`,
            { displayName, organizationId },
            authHeader()
        );
        return res?.data?.data;
    },

    async install(
        id: string,
        body: {
            organizationId?: string;
            name?: string;
            title?: string;
            isPACT?: boolean;
            version?: number;
        }
    ): Promise<InstallResponse> {
        try {
            const res = await AxiosRequest(
                "POST",
                `${base()}/${id}/install`,
                body,
                authHeader()
            );
            return { kind: "installed", data: res?.data?.data };
        } catch (err) {
            if (axios.isAxiosError(err) && err.response?.status === 409) {
                return {
                    kind: "alreadyInstalled",
                    data: err.response.data?.data as AlreadyInstalled,
                };
            }
            throw err;
        }
    },

    async create(
        body: Partial<TemplateDetail> & { displayName: string; organizationId?: string }
    ): Promise<TemplateDetail> {
        const res = await AxiosRequest("POST", `${base()}`, body, authHeader());
        return res?.data?.data;
    },

    async update(
        id: string,
        body: Partial<TemplateDetail> & { organizationId?: string }
    ): Promise<TemplateDetail> {
        const res = await AxiosRequest(
            "PATCH",
            `${base()}/${id}`,
            body,
            authHeader()
        );
        return res?.data?.data;
    },

    async remove(id: string, organizationId?: string): Promise<void> {
        const qs = organizationId
            ? `?organizationId=${encodeURIComponent(organizationId)}`
            : "";
        await AxiosRequest(
            "DELETE",
            `${base()}/${id}${qs}`,
            undefined,
            authHeader()
        );
    },
};
